import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

import Banner3 from "../../assets/img/website-images/Blogdetailsbanner3.png";

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
  hidden: { opacity: 0, scale: 1.04 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
  },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children }) {
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  return (
    <motion.p
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: "easeOut" }}
    >
      {children}
    </motion.p>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogThree() {
  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const { ref: titleRef, inView: titleInView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  /* GSAP: hero image clip-path wipe */
  const imgWrapRef = useRef(null);
  useEffect(() => {
    if (heroInView && imgWrapRef.current) {
      gsap.fromTo(
        imgWrapRef.current,
        { clipPath: "inset(0 100% 0 0)", opacity: 1 },
        {
          clipPath: "inset(0 0% 0 0)",
          duration: 1.1,
          ease: "power3.inOut",
          delay: 0.1,
        },
      );
    }
  }, [heroInView]);

  /* GSAP: hero image scroll parallax */
  const heroImgRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!heroImgRef.current) return;
      const rect = heroImgRef.current.getBoundingClientRect();
      const progress = 1 - rect.bottom / (window.innerHeight + rect.height);
      gsap.set(heroImgRef.current, { y: progress * 30 });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* GSAP: reading progress bar */
  const progressRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!progressRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      gsap.set(progressRef.current, { width: `${progress}%` });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Reading progress bar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          background: "transparent",
          zIndex: 9999,
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <div
          ref={progressRef}
          style={{
            height: "100%",
            width: "0%",
            background: "linear-gradient(90deg, #1E5AA8, #F27024)",
            borderRadius: "0 2px 2px 0",
          }}
        />
      </div>

      <section className="project-detail">
        <div className="container">
          {/* ── Hero Image ── */}
          <div className="upper-box" ref={heroRef}>
            <motion.div
              variants={heroVariants}
              initial="hidden"
              animate={heroInView ? "visible" : "hidden"}
              style={{ borderRadius: "12px", overflow: "hidden" }}
            >
              <div
                ref={imgWrapRef}
                style={{ overflow: "hidden", borderRadius: "12px" }}
              >
                <figure
                  className="image"
                  style={{ margin: 0, overflow: "hidden" }}
                >
                  <img
                    ref={heroImgRef}
                    src={Banner3}
                    alt="Blog Banner"
                    style={{
                      display: "block",
                      width: "100%",
                      transformOrigin: "center",
                    }}
                  />
                </figure>
              </div>
            </motion.div>
          </div>

          {/* ── Article Content ── */}
          <div className="lower-content2">
            <div className="row">
              <div className="text-column col-lg-12 col-md-12 col-sm-12">
                <div className="s-about-content">
                  {/* Title */}
                  <motion.h2
                    ref={titleRef}
                    variants={titleVariants}
                    initial="hidden"
                    animate={titleInView ? "visible" : "hidden"}
                  >
                    A Day in the Life of a Pharmacy Student: What to Expect
                  </motion.h2>

                  <AnimPara delay={0}>
                    Studying pharmacy is an exciting and rewarding journey that
                    combines classroom learning, laboratory practice, and
                    real-world healthcare exposure. Many students and parents
                    often wonder what a typical day looks like for a pharmacy
                    student. Understanding daily routines can help students
                    prepare for the academic responsibilities and practical
                    experiences that shape their professional growth in the
                    pharmaceutical field.
                  </AnimPara>

                  <AnimPara delay={0}>
                    A typical day usually begins with classroom lectures where
                    students learn the theoretical foundations of pharmaceutical
                    sciences. Subjects such as pharmaceutics, pharmacology,
                    pharmaceutical chemistry, and human anatomy form the core of
                    pharmacy education. These lectures provide essential
                    knowledge about medicines, their effects on the human body,
                    and how they are developed and used safely in healthcare
                    settings.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Laboratory sessions are one of the most engaging parts of a
                    pharmacy student’s day. During practical classes, students
                    perform experiments, prepare formulations, analyze drug
                    samples, and learn to use specialized laboratory equipment.
                    These hands-on experiences help students understand
                    scientific concepts more clearly and develop technical
                    skills that are required in pharmaceutical industries and
                    healthcare organizations.
                  </AnimPara>

                  <AnimPara delay={0}>
                    In addition to academic learning, pharmacy students often
                    participate in seminars, workshops, and group discussions.
                    These activities encourage teamwork, improve communication
                    skills, and expose students to the latest developments in
                    the pharmaceutical and healthcare industries. Guest lectures
                    by industry professionals and healthcare experts also
                    provide valuable insights into career opportunities and
                    professional expectations.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Another important part of student life involves assignments,
                    project work, and self-study. Pharmacy students spend time
                    reviewing lecture notes, preparing laboratory records, and
                    studying for examinations. Developing disciplined study
                    habits and time management skills helps students stay
                    organized and perform well academically. Many students also
                    use library resources and digital learning platforms to
                    enhance their understanding of complex topics.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Beyond academics, campus life offers opportunities for
                    personal development and social interaction. Students may
                    participate in cultural events, sports activities, and
                    student organizations that promote leadership, creativity,
                    and teamwork. These experiences contribute to a balanced
                    lifestyle and help students build confidence and
                    interpersonal skills that are valuable in professional
                    environments.
                  </AnimPara>

                  <AnimPara delay={0}>
                    Overall, a day in the life of a pharmacy student is dynamic,
                    structured, and full of learning opportunities. From
                    attending lectures and performing laboratory experiments to
                    participating in campus activities, each day brings new
                    experiences that prepare students for successful careers in
                    healthcare and the pharmaceutical industry. With dedication
                    and consistent effort, pharmacy students can build a strong
                    foundation for their future professional journey.
                  </AnimPara>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogThree;
